// Which way each board direction points *on screen*.
//
// A control that asks "which way should this pawn face" and offers `N E S W`
// is asking the player to hold the board's compass in their head while looking
// at it from an eighth of a turn round and a pitch above. The board is turned
// with the mouse and snaps every 90°, so north is up, or right, or down,
// depending on nothing the control says.
//
// **Measured off the projection, never derived from `FACING_YAW`.** That
// constant shipped backwards from Phase 3 until a playtest caught it —
// "East is negative" is the note in CLAUDE.md — precisely because every test
// compared the renderer to the same wrong number. Projecting a square and its
// neighbour asks the thing that actually draws the board, so the answer
// carries the yaw *and* the pitch foreshortening for free, and it cannot
// disagree with what the player is looking at.

import type { Bounds } from "src/api";
import { DIRECTIONS, DIR_OFFSET, type Direction } from "src/shared/board";

export interface Projector {
  (square: number): { x: number; y: number };
}

/**
 * The screen angle of each direction, in radians, clockwise from up.
 *
 * `null` when the board cannot be projected — no renderer yet, a zero-sized
 * canvas. Callers fall back to the compass points, which is wrong in the way
 * the old control was wrong rather than wrong in a new way.
 */
export function facingAngles(
  centre: number,
  project: Projector
): Record<Direction, number> | null {
  const from = project(centre);
  // The renderer answers with a sentinel rather than throwing when it has
  // nothing to project. A real square is never a thousand pixels off the
  // top-left of the window.
  if (from.x <= -999 && from.y <= -999) return null;

  const angles = {} as Record<Direction, number>;
  for (const direction of DIRECTIONS) {
    const to = project(centre + DIR_OFFSET[direction]);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (!dx && !dy) return null;
    // Clockwise from up, which is what a CSS `rotate` wants: an arrow drawn
    // pointing up is turned by this to point along the direction.
    angles[direction] = Math.atan2(dx, -dy);
  }
  return angles;
}

/**
 * A square in the middle of the board, to measure the on-screen directions
 * from. Any square would do — the projection is affine — but the middle is the
 * one guaranteed to be somewhere sensible on a ragged board.
 */
export function centreSquare(bounds: Bounds): number {
  const { minRank, maxRank, minFile, maxFile } = bounds;
  const rank = (minRank + maxRank) >> 1;
  const file = (minFile + maxFile) >> 1;
  return ((rank + 512) << 10) | (file + 512);
}

/**
 * Has the board turned enough to be worth re-rendering the facing dial?
 *
 * A rotation eases over many frames, so without this the dial would set React
 * state sixty times a second for the length of every camera move. A degree is
 * far below what a 22px arrow can show.
 */
export function turnedEnough(
  old: Record<Direction, number> | null,
  next: Record<Direction, number> | null
): boolean {
  if (!next) return false;
  if (!old) return true;
  for (const direction of DIRECTIONS) {
    if (Math.abs(old[direction] - next[direction]) > 0.017) return true;
  }
  return false;
}

/** Where a control for `direction` sits on a dial of this radius, in CSS px. */
export function dialOffset(
  angle: number,
  radius: number
): { x: number; y: number } {
  return { x: Math.sin(angle) * radius, y: -Math.cos(angle) * radius };
}
